/* ============================================================================
   Product page (2026 design). Loaded by sections/kc-product.liquid,
   kc-product-recs.liquid and kc-recently-viewed.liquid; it runs once.

   <kc-product>  the hero: photo viewer (with zoom), quantity pill, option
                 pills, add to cart (through the theme's cart drawer),
                 folder tabs and the free-shipping meter.
   <kc-recent>   "Recently viewed": remembers product pages in this browser
                 and shows the last few, newest first.
   [data-kc-more] a "Show more" button that reveals the rest of a grid.
   ============================================================================ */
(() => {
  if (window.kcProductPage) return;
  window.kcProductPage = true;

  const money = (cents) => {
    try {
      return theme.Currency.formatMoney(cents, theme.shopSettings.moneyFormat);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  const strings = () => (window.theme && theme.variantStrings) || {
    addToCart: 'Add to cart',
    soldOut: 'Sold out',
    unavailable: 'Unavailable'
  };

  /* ------------------------------------------------------------------------
     <kc-product>
     ------------------------------------------------------------------------ */
  class KcProduct extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-kc-form]');
      this.idInput = this.form && this.form.querySelector('input[name="id"]');
      this.qty = this.querySelector('[data-kc-qty]');
      this.atc = this.querySelector('[data-kc-atc]');
      this.atcLabel = this.querySelector('[data-kc-atc-label]');
      this.error = this.querySelector('[data-kc-error]');
      this.price = this.querySelector('[data-kc-price]');
      this.viewer = this.querySelector('[data-kc-viewer]');
      this.thumbs = Array.from(this.querySelectorAll('[data-kc-th]'));
      this.mainImg = this.querySelector('.kc-pp__main');
      this.meter = this.querySelector('[data-kc-meter]');
      this.threshold = Number(this.dataset.threshold) || 0;
      this.cartTotal = Number(this.dataset.cartTotal) || 0;

      const json = this.querySelector('[data-kc-variants]');
      try {
        this.variants = json ? JSON.parse(json.textContent) : [];
      } catch (e) {
        this.variants = [];
      }
      this.variant = this.variants.find((v) => this.idInput && String(v.id) === this.idInput.value)
        || this.variants[0] || null;

      this.bindGallery();
      this.bindQty();
      this.bindOptions();
      this.bindForm();
      this.bindTabs();
      this.bindDescScroll();

      this.onCartChange = () => this.refreshCart();
      document.addEventListener('ajaxProduct:added', this.onCartChange);
    }

    disconnectedCallback() {
      document.removeEventListener('ajaxProduct:added', this.onCartChange);
    }

    /* --- photos ----------------------------------------------------------- */
    bindGallery() {
      this.querySelectorAll('[data-kc-open]').forEach((tile) => {
        tile.addEventListener('click', () => this.openViewer(Number(tile.dataset.kcOpen) || 0));
      });
      if (!this.viewer) return;

      this.big = this.viewer.querySelector('[data-kc-big]');
      this.stage = this.viewer.querySelector('[data-kc-stage]');
      this.cap = this.viewer.querySelector('[data-kc-cap]');
      this.num = this.viewer.querySelector('[data-kc-n]');
      this.zoomInBtn = this.viewer.querySelector('[data-kc-zoom="in"]');
      this.zoomOutBtn = this.viewer.querySelector('[data-kc-zoom="out"]');
      this.capText = this.viewer.dataset.caption || '';
      this.index = 0;
      this.z = { s: 1, x: 0, y: 0 };

      this.viewer.querySelector('[data-kc-close]').addEventListener('click', () => this.viewer.close());
      const prev = this.viewer.querySelector('[data-kc-prev]');
      const next = this.viewer.querySelector('[data-kc-next]');
      if (prev) prev.addEventListener('click', () => this.show(this.index - 1));
      if (next) next.addEventListener('click', () => this.show(this.index + 1));
      this.thumbs.forEach((th) => th.addEventListener('click', () => this.show(Number(th.dataset.kcTh))));
      if (this.zoomInBtn) this.zoomInBtn.addEventListener('click', () => this.zoomBy(1.6));
      if (this.zoomOutBtn) this.zoomOutBtn.addEventListener('click', () => this.zoomBy(1 / 1.6));

      // the dark area around the photo closes it
      this.viewer.addEventListener('click', (e) => {
        const t = e.target;
        if (t === this.viewer || t.classList.contains('kc-pv__in') || t.classList.contains('kc-pv__mid')) this.viewer.close();
      });
      this.viewer.addEventListener('close', () => this.resetZoom());
      // on the document: a button that disables itself drops the focus
      document.addEventListener('keydown', (e) => {
        if (this.viewer.open && !e.defaultPrevented && !e.altKey && !e.ctrlKey && !e.metaKey) this.onViewerKey(e);
      });
      window.addEventListener('resize', () => {
        if (!this.viewer.open) return;
        this.fitThumbs();
        if (this.z.s > 1) this.applyZoom();
      });

      this.bindZoom();
    }

    onViewerKey(e) {
      const zoomed = this.z.s > 1.001;
      switch (e.key) {
        case 'ArrowLeft':
          if (zoomed) this.panBy(80, 0); else this.show(this.index - 1);
          break;
        case 'ArrowRight':
          if (zoomed) this.panBy(-80, 0); else this.show(this.index + 1);
          break;
        case 'ArrowUp':
          if (!zoomed) return;
          this.panBy(0, 80);
          break;
        case 'ArrowDown':
          if (!zoomed) return;
          this.panBy(0, -80);
          break;
        case '+':
        case '=':
          this.zoomBy(1.6);
          break;
        case '-':
        case '_':
          this.zoomBy(1 / 1.6);
          break;
        case '0':
          this.resetZoom();
          break;
        default:
          return;
      }
      e.preventDefault();
    }

    /* --- zoom -------------------------------------------------------------
       The <img> fills the stage (object-fit: contain) and is moved with
       translate() scale() from its top-left corner. z.s is the scale (1 =
       the whole photo fits), z.x / z.y the offset in stage pixels. Click or
       double-tap jumps to the photo's full detail and back; the wheel,
       pinching and the +/- buttons zoom further; dragging pans. The first
       zoom swaps in the original upload. */
    bindZoom() {
      const st = this.stage;
      if (!st) return;
      const pts = new Map();
      let start = null;
      let pinch = null;
      let lastTap = 0;

      const spread = () => {
        const [a, b] = [...pts.values()];
        return Math.hypot(a.x - b.x, a.y - b.y) || 1;
      };
      const middle = () => {
        const [a, b] = [...pts.values()];
        const r = st.getBoundingClientRect();
        return [(a.x + b.x) / 2 - r.left, (a.y + b.y) / 2 - r.top];
      };

      st.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        st.setPointerCapture(e.pointerId);
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pts.size === 2) {
          pinch = { d: spread(), s: this.z.s };
          start = null;
        } else if (pts.size === 1) {
          start = { x: e.clientX, y: e.clientY, zx: this.z.x, zy: this.z.y, moved: false };
        }
      });

      st.addEventListener('pointermove', (e) => {
        if (!pts.has(e.pointerId)) return;
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pinch && pts.size === 2) {
          this.viewer.classList.add('is-dragging');
          const [cx, cy] = middle();
          this.zoomTo(pinch.s * (spread() / pinch.d), cx, cy);
          return;
        }
        if (!start) return;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        if (!start.moved && Math.hypot(dx, dy) < 6) return;
        start.moved = true;
        if (this.z.s > 1.001) {
          this.viewer.classList.add('is-dragging');
          this.z.x = start.zx + dx;
          this.z.y = start.zy + dy;
          this.applyZoom();
        }
      });

      const end = (e) => {
        if (!pts.has(e.pointerId)) return;
        pts.delete(e.pointerId);
        if (pinch) {
          // after a pinch, the finger left behind carries on panning
          pinch = null;
          const rest = [...pts.values()][0];
          start = rest ? { x: rest.x, y: rest.y, zx: this.z.x, zy: this.z.y, moved: true } : null;
          if (!rest) this.viewer.classList.remove('is-dragging');
          return;
        }
        this.viewer.classList.remove('is-dragging');
        const s = start;
        start = null;
        if (!s || e.type === 'pointercancel') return;
        const r = st.getBoundingClientRect();
        const px = e.clientX - r.left;
        const py = e.clientY - r.top;
        if (!s.moved) {
          if (e.pointerType === 'mouse') {
            this.toggleZoom(px, py);
          } else if (Date.now() - lastTap < 320) {
            lastTap = 0;
            this.toggleZoom(px, py);
          } else {
            lastTap = Date.now();
          }
          return;
        }
        // a swipe across the whole photo turns to the next one
        const dx = e.clientX - s.x;
        const dy = e.clientY - s.y;
        if (this.z.s <= 1.001 && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) && this.thumbs.length > 1) {
          this.show(this.index + (dx < 0 ? 1 : -1));
        }
      };
      st.addEventListener('pointerup', end);
      st.addEventListener('pointercancel', end);

      st.addEventListener('wheel', (e) => {
        e.preventDefault();
        const r = st.getBoundingClientRect();
        const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
        this.viewer.classList.add('is-dragging');
        this.zoomBy(Math.exp(-delta * 0.002), e.clientX - r.left, e.clientY - r.top);
        clearTimeout(this.wheelTimer);
        this.wheelTimer = setTimeout(() => this.viewer.classList.remove('is-dragging'), 150);
      }, { passive: false });
    }

    // where the photo sits in the stage while it fits
    fitBox() {
      const W = this.stage.clientWidth || 1;
      const H = this.stage.clientHeight || 1;
      const th = this.thumbs[this.index];
      const ratio = (Number(th && th.dataset.w) || 1) / (Number(th && th.dataset.h) || 1);
      let w = W;
      let h = W / ratio;
      if (h > H) {
        h = H;
        w = H * ratio;
      }
      return { W, H, w, h, x: (W - w) / 2, y: (H - h) / 2 };
    }

    // click / double-tap: one photo pixel per screen pixel, between 2x and 4x
    detailScale() {
      const th = this.thumbs[this.index];
      const px = (Number(th && th.dataset.w) || 0) / (this.fitBox().w * (window.devicePixelRatio || 1));
      return Math.max(2, Math.min(4, px));
    }

    maxScale() {
      const th = this.thumbs[this.index];
      const px = (Number(th && th.dataset.w) || 0) / this.fitBox().w;
      return Math.max(4, Math.min(8, px * 1.5));
    }

    applyZoom() {
      const b = this.fitBox();
      const z = this.z;
      // keep the photo covering the window; centre it along an axis it doesn't fill
      const clamp = (t, size, off, view) => {
        const span = size * z.s;
        if (span <= view) return (view - span) / 2 - off * z.s;
        return Math.min(-off * z.s, Math.max(view - (off + size) * z.s, t));
      };
      z.x = clamp(z.x, b.w, b.x, b.W);
      z.y = clamp(z.y, b.h, b.y, b.H);
      const zoomed = z.s > 1.001;
      this.big.style.transform = zoomed ? `translate(${z.x}px, ${z.y}px) scale(${z.s})` : '';
      this.viewer.classList.toggle('is-zoomed', zoomed);
      this.setZoomButton(this.zoomOutBtn, !zoomed, this.zoomInBtn);
      this.setZoomButton(this.zoomInBtn, z.s >= this.maxScale() - 0.001, this.zoomOutBtn);
      if (zoomed) this.loadOriginal();
    }

    // disable a zoom button at its limit, handing the focus to the other one
    setZoomButton(btn, off, other) {
      if (!btn) return;
      if (off && document.activeElement === btn && other && !other.disabled) other.focus();
      btn.disabled = off;
    }

    // zoom to scale s, keeping the stage point (px, py) where it is
    zoomTo(s, px, py) {
      if (!this.stage) return;
      const z = this.z;
      const next = Math.max(1, Math.min(this.maxScale(), s));
      if (px === undefined) {
        const b = this.fitBox();
        px = b.W / 2;
        py = b.H / 2;
      }
      z.x = px - (px - z.x) * (next / z.s);
      z.y = py - (py - z.y) * (next / z.s);
      z.s = next;
      this.applyZoom();
    }

    zoomBy(factor, px, py) {
      this.zoomTo(this.z.s * factor, px, py);
    }

    panBy(dx, dy) {
      this.z.x += dx;
      this.z.y += dy;
      this.applyZoom();
    }

    toggleZoom(px, py) {
      if (this.z.s > 1.001) this.resetZoom();
      else this.zoomTo(this.detailScale(), px, py);
    }

    resetZoom() {
      this.z = { s: 1, x: 0, y: 0 };
      if (this.big && this.stage) this.applyZoom();
    }

    loadOriginal() {
      const th = this.thumbs[this.index];
      const url = th && th.dataset.zoom;
      if (!url || this.big.dataset.full === url || this.loadingFull === url) return;
      this.loadingFull = url;
      const idx = this.index;
      const img = new Image();
      img.src = url;
      const done = () => {
        this.loadingFull = null;
        if (this.index !== idx || !this.viewer.open) return;
        this.big.removeAttribute('srcset');
        this.big.src = url;
        this.big.dataset.full = url;
      };
      (img.decode ? img.decode() : Promise.resolve()).then(done, () => { this.loadingFull = null; });
    }

    // Wide screens: enough columns of thumbnails to show every photo beside
    // the polaroid without scrolling (two at least). Worked out from the
    // window size with the same sums as --pol in the CSS, before the viewer
    // opens, so the polaroid never shifts once it's up. The photo keeps at
    // least 360px; past that the thumbnails get smaller instead.
    fitThumbs() {
      const style = this.viewer.style;
      const n = this.thumbs.length;
      if (n < 2 || !window.matchMedia('(min-width: 990px)').matches) {
        style.removeProperty('--kc-pv-cols');
        style.removeProperty('--kc-pv-th');
        return;
      }
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let pick = null;
      for (const th of [52, 44, 36]) {
        const unit = th + 8;
        for (let cols = 2; cols <= 6; cols++) {
          const pol = Math.min(900, vw - 268 - 2 * cols * unit, Math.max(320, vh - 210));
          if (pol < 360) break;
          pick = { th, cols };
          // the polaroid's frame and caption add 64px; a row takes th + 8
          if (cols * Math.max(1, Math.floor((pol + 64) / unit)) >= n) {
            style.setProperty('--kc-pv-cols', cols);
            style.setProperty('--kc-pv-th', `${th}px`);
            return;
          }
        }
      }
      // a very short window with a very long gallery: the closest fit
      style.setProperty('--kc-pv-cols', pick ? pick.cols : 2);
      style.setProperty('--kc-pv-th', `${pick ? pick.th : 36}px`);
    }

    openViewer(i) {
      if (!this.viewer) return;
      this.fitThumbs();
      if (typeof this.viewer.showModal === 'function') {
        if (!this.viewer.open) this.viewer.showModal();
      } else {
        this.viewer.setAttribute('open', '');
      }
      this.show(i);
    }

    show(i) {
      const count = this.thumbs.length;
      if (!count) return;
      this.index = ((i % count) + count) % count;
      const th = this.thumbs[this.index];
      // a new photo starts whole, without animating out of the last zoom
      this.viewer.classList.add('is-dragging');
      delete this.big.dataset.full;
      this.big.srcset = th.dataset.srcset || '';
      this.big.src = th.dataset.src;
      this.big.alt = th.dataset.alt || '';
      this.resetZoom();
      requestAnimationFrame(() => this.viewer.classList.remove('is-dragging'));
      if (this.num) this.num.textContent = String(this.index + 1);
      if (this.cap && count > 1) {
        this.cap.textContent = (this.capText || '[n] / [count]')
          .replace('[n]', this.index + 1)
          .replace('[count]', count);
      }
      this.thumbs.forEach((t, j) => t.setAttribute('aria-pressed', j === this.index ? 'true' : 'false'));
      // warm the next photo
      const nxt = this.thumbs[(this.index + 1) % count];
      if (nxt && nxt !== th) {
        const img = new Image();
        img.sizes = this.big.sizes;
        img.srcset = nxt.dataset.srcset || '';
        img.src = nxt.dataset.src;
      }
    }

    /* --- quantity --------------------------------------------------------- */
    bindQty() {
      if (!this.qty) return;
      this.querySelectorAll('[data-kc-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = Number(this.qty.step) || 1;
          this.setQty((Number(this.qty.value) || 0) + step * Number(btn.dataset.kcStep));
        });
      });
      this.qty.addEventListener('change', () => this.setQty(Number(this.qty.value)));
      this.setQty(Number(this.qty.value));
    }

    setQty(n) {
      const min = Number(this.qty.min) || 1;
      const max = this.qty.max ? Number(this.qty.max) : Infinity;
      let v = Number.isFinite(n) ? Math.round(n) : min;
      v = Math.max(min, Math.min(max, v));
      this.qty.value = v;
      const [less, more] = this.querySelectorAll('[data-kc-step]');
      if (less) less.disabled = v <= min;
      if (more) more.disabled = v >= max;
      this.updateMeter();
    }

    /* --- options ---------------------------------------------------------- */
    bindOptions() {
      this.optionInputs = Array.from(this.querySelectorAll('[data-kc-option]'));
      if (!this.optionInputs.length || !this.variants.length) return;
      this.optionInputs.forEach((input) => input.addEventListener('change', () => this.onOptionChange()));
      this.markSoldOut(this.selectedOptions());
    }

    // cross out values that are sold out with the other picks
    markSoldOut(picked) {
      this.optionInputs.forEach((input) => {
        const i = Number(input.dataset.kcOption);
        const ok = this.variants.some((v) => v.available && v.options[i] === input.value
          && v.options.every((value, k) => k === i || value === picked[k]));
        input.closest('.kc-pp__val').classList.toggle('is-out', !ok);
      });
    }

    selectedOptions() {
      const picked = [];
      this.optionInputs.forEach((input) => {
        if (input.checked) picked[Number(input.dataset.kcOption)] = input.value;
      });
      return picked;
    }

    onOptionChange() {
      const picked = this.selectedOptions();
      const match = this.variants.find((v) => v.options.every((value, i) => value === picked[i]));
      this.variant = match || null;
      const s = strings();

      if (this.idInput) {
        this.idInput.value = match ? match.id : '';
        // Shopify's "Buy it now" follows the form's id field
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.clearError();

      if (!match) {
        this.atc.disabled = true;
        this.atcLabel.textContent = s.unavailable;
      } else {
        this.atc.disabled = !match.available;
        this.atcLabel.textContent = match.available ? s.addToCart : s.soldOut;
        this.renderPrice(match);
        this.showVariantPhoto(match);
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', match.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) { /* keep the old URL */ }
      }

      this.markSoldOut(picked);
      this.updateMeter();
    }

    renderPrice(v) {
      if (!this.price) return;
      const regular = this.price.dataset.labelRegular || 'Regular price';
      const sale = this.price.dataset.labelSale || 'Sale price';
      this.price.innerHTML = v.compare_at_price > v.price
        ? `<s><span class="kc-vh">${regular}</span>${money(v.compare_at_price)}</s> <span class="kc-vh">${sale}</span>${money(v.price)}`
        : money(v.price);
    }

    showVariantPhoto(v) {
      const id = v.featured_media && v.featured_media.id;
      if (!id || !this.mainImg) return;
      const k = this.thumbs.findIndex((t) => t.dataset.mediaId === String(id));
      if (k < 0) return;
      const th = this.thumbs[k];
      this.mainImg.srcset = th.dataset.srcset || '';
      this.mainImg.src = th.dataset.src;
      this.mainImg.alt = th.dataset.alt || '';
      const tile = this.mainImg.closest('[data-kc-open]');
      if (tile) tile.dataset.kcOpen = k;
    }

    /* --- add to cart ------------------------------------------------------ */
    bindForm() {
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => {
        const cart = document.querySelector('mini-cart');
        const drawer = window.theme && theme.shopSettings && theme.shopSettings.cartDrawer;
        // no drawer: let the form post and go to the cart page
        if (!cart || !drawer || document.body.classList.contains('template-cart')) return;
        e.preventDefault();
        if (this.atc.disabled || this.atc.classList.contains('is-loading')) return;
        this.add(cart);
      });
    }

    async add(cart) {
      this.clearError();
      this.atc.classList.add('is-loading');
      this.atc.setAttribute('aria-busy', 'true');

      const body = new FormData(this.form);
      body.append('sections', cart.getSectionsToRender().map((s) => s.id));
      body.append('sections_url', window.location.pathname);
      if (cart.setActiveElement) cart.setActiveElement(document.activeElement);

      try {
        const res = await fetch(theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body
        });
        const data = await res.json();
        if (data.status) {
          this.showError(data.description || data.message);
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, { source: 'kc-product', productVariantId: body.get('id'), errors: data.errors || data.description, message: data.message });
          }
          return;
        }
        cart.renderContents(data);
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kc-product', productVariantId: body.get('id'), cartData: data });
        }
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));
        this.flashAdded();
      } catch (err) {
        console.error(err);
        this.showError(window.theme && theme.cartStrings ? theme.cartStrings.error : 'Something went wrong. Please try again.');
      } finally {
        this.atc.classList.remove('is-loading');
        this.atc.removeAttribute('aria-busy');
      }
    }

    flashAdded() {
      const label = this.atcLabel.textContent;
      const done = this.atc.dataset.added || 'Added ✓';
      this.atcLabel.textContent = done;
      clearTimeout(this.addedTimer);
      this.addedTimer = setTimeout(() => {
        if (this.atcLabel.textContent === done) this.atcLabel.textContent = label;
      }, 2500);
    }

    showError(message) {
      if (!this.error || !message) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
    }

    /* --- free-shipping meter ---------------------------------------------- */
    async refreshCart() {
      if (!this.meter) return;
      try {
        const res = await fetch(`${theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        this.cartTotal = Number(cart.total_price) || 0;
        this.updateMeter();
      } catch (e) { /* keep the last numbers */ }
    }

    updateMeter() {
      if (!this.meter || !this.threshold) return;
      const price = this.variant ? Number(this.variant.price) || 0 : 0;
      const qty = this.qty ? Number(this.qty.value) || 1 : 1;
      const total = this.cartTotal + price * qty;
      const left = this.threshold - total;
      const pct = left > 0 ? Math.max(0, Math.min(100, (total / this.threshold) * 100)) : 100;
      this.meter.classList.toggle('is-free', left <= 0);
      const amount = this.meter.querySelector('[data-kc-ship-left]');
      if (amount) amount.textContent = money(Math.max(0, left));
      const bar = this.meter.querySelector('[data-kc-bar]');
      if (bar) bar.style.width = pct + '%';
    }

    /* --- folder tabs ------------------------------------------------------ */
    bindTabs() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!this.tabs.length) return;
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.pickTab(i));
        tab.addEventListener('keydown', (e) => {
          let j = null;
          if (e.key === 'ArrowRight') j = (i + 1) % this.tabs.length;
          if (e.key === 'ArrowLeft') j = (i - 1 + this.tabs.length) % this.tabs.length;
          if (e.key === 'Home') j = 0;
          if (e.key === 'End') j = this.tabs.length - 1;
          if (j === null) return;
          e.preventDefault();
          this.pickTab(j);
          this.tabs[j].focus();
        });
      });
    }

    /* --- long descriptions ------------------------------------------------
       The sheets share one height (the tallest), so a long description
       scrolls inside its sheet (max-height in the CSS) rather than
       stretching every other tab. While there's more below, the text fades
       out at the bottom; a scrolling description can take the keyboard
       focus so it scrolls with the arrow keys. */
    bindDescScroll() {
      const rte = this.querySelector('[data-kc-rte]');
      if (!rte) return;
      const update = () => {
        const scrolls = rte.scrollHeight > rte.clientHeight + 1;
        rte.classList.toggle('is-scroll', scrolls);
        rte.classList.toggle('is-end', rte.scrollTop + rte.clientHeight >= rte.scrollHeight - 2);
        if (scrolls) rte.tabIndex = 0;
        else rte.removeAttribute('tabindex');
      };
      update();
      rte.addEventListener('scroll', update, { passive: true });
      rte.addEventListener('load', update, true);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(rte);
    }

    pickTab(i) {
      this.tabs.forEach((tab, j) => {
        const on = i === j;
        tab.classList.toggle('is-on', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.classList.toggle('is-on', on);
      });
    }
  }

  if (!customElements.get('kc-product')) customElements.define('kc-product', KcProduct);

  /* ------------------------------------------------------------------------
     <kc-recent>: "Recently viewed". The list of handles lives in this
     browser only (localStorage); each product comes from /products/x.js.
     ------------------------------------------------------------------------ */
  const RECENT_KEY = 'kc:recent';

  const readRecent = () => {
    try {
      const list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
    } catch (e) {
      return [];
    }
  };

  const writeRecent = (list) => {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 12)));
    } catch (e) { /* private mode: nothing to remember */ }
  };

  const sized = (url, width) => {
    if (!url) return '';
    const u = url.startsWith('//') ? 'https:' + url : url;
    return u + (u.includes('?') ? '&' : '?') + 'width=' + width;
  };

  class KcRecent extends HTMLElement {
    connectedCallback() {
      const current = this.dataset.handle || '';
      const limit = Number(this.dataset.limit) || 6;
      const all = readRecent();
      this.handles = all.filter((h) => h !== current).slice(0, limit);
      if (current) writeRecent([current, ...all.filter((h) => h !== current)]);
      if (!this.handles.length) return;

      if ('IntersectionObserver' in window) {
        // the section is hidden until it has cards, so watch its wrapper
        const host = this.closest('[data-kc-recent-section]');
        const target = (host && host.parentElement) || this;
        const io = new IntersectionObserver((entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          io.disconnect();
          this.load();
        }, { rootMargin: '600px 0px' });
        io.observe(target);
      } else {
        this.load();
      }
    }

    async load() {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const results = await Promise.all(this.handles.map((h) =>
        fetch(`${root}products/${encodeURIComponent(h)}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)));

      // forget products that are gone
      const gone = this.handles.filter((h, i) => !results[i]);
      if (gone.length) writeRecent(readRecent().filter((h) => !gone.includes(h)));

      const items = results.filter(Boolean);
      const list = this.querySelector('[data-kc-recent-list]');
      if (!items.length || !list) return;

      const view = new URLSearchParams(window.location.search).get('view');
      const from = this.dataset.from || 'from';
      list.replaceChildren(...items.map((p) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'kc-rv__card';
        a.href = p.url + (view ? `?view=${encodeURIComponent(view)}` : '');

        const ph = document.createElement('span');
        ph.className = 'kc-rv__ph';
        if (p.featured_image) {
          const img = document.createElement('img');
          img.src = sized(p.featured_image, 360);
          img.srcset = [180, 270, 360, 540].map((w) => `${sized(p.featured_image, w)} ${w}w`).join(', ');
          img.sizes = '(min-width: 990px) 180px, 45vw';
          img.alt = '';
          img.loading = 'lazy';
          img.width = 360;
          img.height = 360;
          ph.append(img);
        }

        const t = document.createElement('span');
        t.className = 'kc-rv__t';
        t.textContent = p.title;

        const price = document.createElement('span');
        price.className = 'kc-rv__p';
        price.textContent = (p.price_varies ? from + ' ' : '') + money(p.price);

        a.append(ph, t, price);
        li.append(a);
        return li;
      }));

      const host = this.closest('[data-kc-recent-section]') || this;
      host.hidden = false;
    }
  }

  if (!customElements.get('kc-recent')) customElements.define('kc-recent', KcRecent);

  /* ------------------------------------------------------------------------
     "Show more" under a grid that hides its extra cards on phones.
     ------------------------------------------------------------------------ */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-kc-more]');
    if (!btn) return;
    const grid = document.getElementById(btn.getAttribute('aria-controls'));
    if (!grid) return;
    grid.classList.add('is-all');
    btn.setAttribute('aria-expanded', 'true');
    const first = grid.querySelector('.is-extra a[href]');
    (btn.closest('.kc-more') || btn).remove();
    if (first) first.focus({ preventScroll: true });
  });
})();
